import { flow } from '@1x2network/flow'
import { createSpine, Spine, SpineSpecification } from '@1x2network/pixi_assets'
import { Container } from 'pixi.js'
import { Signal } from 'typed-signals'

interface MiddleReelOptions {
    spec: MiddleReelSpecification
    backgroundParent: Container
    foregroundParent: Container
}

export class MiddleReel {
    public readonly backgroundSpine: Spine
    public readonly foregroundSpine: Spine
    private spec: MiddleReelSpecification

    public readonly onAnimate = new Signal()

    constructor({ spec, backgroundParent, foregroundParent }: MiddleReelOptions) {
        this.spec = spec
        this.backgroundSpine = createSpine({ spec: spec.background, parent: backgroundParent })
        this.foregroundSpine = createSpine({ spec: spec.foreground, parent: foregroundParent })
    }

    public async in(): Promise<void> {
        const animations = this.spec.animations
        this.backgroundSpine.visible = true
        this.foregroundSpine.visible = true

        this.onAnimate.emit()

        // in
        await Promise.all([
            flow.wait(this.backgroundSpine.play(animations.in)),
            flow.wait(this.foregroundSpine.play(animations.in)),
        ])

        // loop
        this.backgroundSpine.play(this.spec.animations.loop, 0, true)
        this.foregroundSpine.play(this.spec.animations.loop, 0, true)
    }

    public async out(): Promise<void> {
        const animations = this.spec.animations
        await Promise.all([
            flow.wait(this.backgroundSpine.play(animations.out)),
            flow.wait(this.foregroundSpine.play(animations.out)),
        ])
        this.backgroundSpine.visible = false
        this.foregroundSpine.visible = false
    }

    public show(): void {
        this.backgroundSpine.visible = true
        this.foregroundSpine.visible = true
        this.backgroundSpine.play(this.spec.animations.loop, 0, true)
        this.foregroundSpine.play(this.spec.animations.loop, 0, true)
    }

    public hide(): void {
        this.backgroundSpine.visible = false
        this.foregroundSpine.visible = false
    }
}

export interface MiddleReelSpecification {
    background: SpineSpecification
    foreground: SpineSpecification
    animations: {
        in: string
        out: string
        loop: string
    }
}
